import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import { mockTemplates } from './mockData';

const TemplateSelectorMock = ({ onSelectTemplate, currentCriteria, onSaveAsTemplate }) => {
  const [templates, setTemplates] = useState(mockTemplates);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateCategory, setNewTemplateCategory] = useState('Use Case-Specific');
  const [filterCategory, setFilterCategory] = useState('all');

  const categories = ['All', 'Industry-Specific', 'Stage-Specific', 'Use Case-Specific'];

  const handleToggleFavorite = (templateId) => {
    setTemplates(templates.map(t => 
      t.id === templateId ? { ...t, favorite: !t.favorite } : t
    ));
  };

  const handleDeleteTemplate = (templateId) => {
    setTemplates(templates.filter(t => t.id !== templateId));
  };

  const handleSelectTemplate = (template) => {
    onSelectTemplate(template);
    // Update usage count
    setTemplates(templates.map(t => 
      t.id === template.id 
        ? { ...t, usageCount: t.usageCount + 1, lastUsed: new Date().toISOString().split('T')[0] }
        : t
    ));
  };

  const handlePreviewTemplate = (template) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  const handleSaveTemplate = () => {
    if (newTemplateName && currentCriteria) {
      const newTemplate = {
        id: Date.now(),
        name: newTemplateName,
        description: newTemplateDescription,
        category: newTemplateCategory,
        favorite: false,
        criteria: currentCriteria,
        enrichmentColumns: currentCriteria.enrichmentColumns || [],
        usageCount: 0,
        lastUsed: null,
      };
      
      setTemplates([newTemplate, ...templates]);
      onSaveAsTemplate?.(newTemplate);
      setSaveDialogOpen(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
    }
  };

  const filteredTemplates = filterCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category.toLowerCase() === filterCategory.toLowerCase());

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Industry-Specific': return <BusinessIcon />;
      case 'Stage-Specific': return <TrendingUpIcon />;
      default: return <HistoryIcon />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Industry-Specific': return 'primary';
      case 'Stage-Specific': return 'success';
      default: return 'info';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">Saved Templates</Typography>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={() => setSaveDialogOpen(true)}
          disabled={!currentCriteria}
        >
          Save Current Setup
        </Button>
      </Stack>

      {/* Filter Tabs */}
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        {categories.map((category) => (
          <Button
            key={category}
            variant={filterCategory === category.toLowerCase() || (filterCategory === 'all' && category === 'All') ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setFilterCategory(category === 'All' ? 'all' : category.toLowerCase())}
          >
            {category}
          </Button>
        ))}
      </Stack>

      {/* Templates Grid */}
      <Grid container spacing={2}>
        {filteredTemplates.length === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">
              No templates found for the selected category. Create your first template by configuring criteria and saving them.
            </Alert>
          </Grid>
        ) : (
          filteredTemplates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card 
                variant="outlined"
                sx={{
                  height: '100%',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2,
                  },
                }}
              >
                <CardContent>
                  {/* Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" noWrap>
                        {template.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {template.description}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleFavorite(template.id)}
                      color={template.favorite ? 'error' : 'default'}
                    >
                      {template.favorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    </IconButton>
                  </Stack>

                  {/* Category and Stats */}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Chip
                      icon={getCategoryIcon(template.category)}
                      label={template.category}
                      size="small"
                      color={getCategoryColor(template.category)}
                      variant="outlined"
                    />
                    <Chip
                      label={`${template.usageCount} uses`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>

                  {/* Enrichment Info */}
                  {template.enrichmentColumns && template.enrichmentColumns.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Enrichment: {template.enrichmentColumns.length} columns
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        {template.enrichmentColumns.slice(0, 2).map((col) => (
                          <Chip key={col.id} label={col.name} size="small" />
                        ))}
                        {template.enrichmentColumns.length > 2 && (
                          <Chip label={`+${template.enrichmentColumns.length - 2} more`} size="small" />
                        )}
                      </Stack>
                    </Box>
                  )}

                  {/* Last Used */}
                  {template.lastUsed && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Last used: {template.lastUsed}
                    </Typography>
                  )}

                  {/* Actions */}
                  <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                    <Button
                      size="small"
                      onClick={() => handleSelectTemplate(template)}
                      variant="contained"
                      sx={{ flex: 1 }}
                    >
                      Apply Template
                    </Button>
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={() => handlePreviewTemplate(template)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTemplate(template.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Template Preview: {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {selectedTemplate.description}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip
                    label={selectedTemplate.category}
                    size="small"
                    color={getCategoryColor(selectedTemplate.category)}
                  />
                  <Chip label={`${selectedTemplate.usageCount} uses`} size="small" />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" color="error.main" gutterBottom>
                  Must-Have Criteria
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {selectedTemplate.criteria.mustHaves}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" color="success.main" gutterBottom>
                  Nice-to-Have Criteria
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {selectedTemplate.criteria.niceToHaves}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" color="warning.main" gutterBottom>
                  Disqualification Criteria
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {selectedTemplate.criteria.disqualifications}
                </Typography>
              </Box>

              {selectedTemplate.enrichmentColumns && selectedTemplate.enrichmentColumns.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Enrichment Columns ({selectedTemplate.enrichmentColumns.length})
                  </Typography>
                  <Stack spacing={1}>
                    {selectedTemplate.enrichmentColumns.map((col) => (
                      <Box key={col.id} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {col.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {col.description}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Chip label={col.dataFormat} size="small" />
                          <Chip 
                            label={col.useWebSearch ? 'Web Search' : 'Content Extraction'} 
                            size="small"
                            color={col.useWebSearch ? 'primary' : 'default'}
                          />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              handleSelectTemplate(selectedTemplate);
              setPreviewOpen(false);
            }}
            variant="contained"
          >
            Apply Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Template</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Template Name"
              placeholder="e.g., Enterprise SaaS Companies"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              required
            />

            <TextField
              fullWidth
              label="Description"
              placeholder="Brief description of this template's purpose"
              value={newTemplateDescription}
              onChange={(e) => setNewTemplateDescription(e.target.value)}
              multiline
              rows={2}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newTemplateCategory}
                onChange={(e) => setNewTemplateCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="Industry-Specific">Industry-Specific</MenuItem>
                <MenuItem value="Stage-Specific">Stage-Specific</MenuItem>
                <MenuItem value="Use Case-Specific">Use Case-Specific</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info">
              Your current criteria configuration will be saved as a reusable template.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={!newTemplateName}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateSelectorMock;